/**
 * @poc-to-production
 *
 * SheetViewer — Forge Viewer mount + violation highlight / overlay.
 */
import { useEffect, useRef } from "preact/hooks";
import { isAuthDisabled } from "../auth/apsConfig";
import { useAutodeskViewer } from "../hooks/useAutodeskViewer";
import { useEffectiveSelectedViolation } from "../hooks/useEffectiveSelectedViolation";
import { useActiveSheetId, useIsUniversalView } from "../lib/activeSheets";
import { useAccBrowseStore } from "../stores/accBrowseStore";
import { useAccModelStore } from "../stores/accModelStore";
import { useEngineStore } from "../stores/engineStore";
import { useFactPreviewStore } from "../stores/factPreviewStore";
import { visualizeForViolation } from "../stores/geometryStore";
import { useViewerStore } from "../stores/viewerStore";
import { useViolationsStore } from "../stores/violationsStore";
import { getGeometryOverlayExtension } from "../viewer/extensions/GeometryOverlayExtension";
import { fitViolationSelection } from "../viewer/violationFocus";
import { AccAnalysisOverlay, AccEmptyViewerPrompt } from "./AccAnalysisOverlay";
import { AccDocumentViewer } from "./AccDocumentViewer";
import { MissingFactPrompt } from "./MissingFactPrompt";

export function SheetViewer() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const accMode = !isAuthDisabled();
  const accSelection = useAccBrowseStore(s => s.selection);
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const showEmptyPrompt = accMode && !accSelection;

  useAutodeskViewer({ containerRef, enabled: !showEmptyPrompt });

  const ready = useViewerStore(s => s.ready);
  const viewer = useViewerStore(s => s.viewer);
  const activeSheetId = useActiveSheetId();
  const onUniversalView = useIsUniversalView();

  const selected = useEffectiveSelectedViolation();
  const selectedId = useViolationsStore(s => s.selectedViolationId);
  const previewFacts = useFactPreviewStore(s => s.previewFacts);
  const elements = useEngineStore(s => s.elements);

  useEffect(() => {
    if (!viewer || !ready) return;

    if (!selected) {
      void getGeometryOverlayExtension(viewer).then(ext => ext?.clear());
      return;
    }

    if (
      !onUniversalView &&
      selected.preferredSheetId &&
      selected.preferredSheetId !== activeSheetId
    ) {
      void getGeometryOverlayExtension(viewer).then(ext => ext?.clear());
      return;
    }

    const dbId = selected.elementDbId;
    if (typeof dbId !== "number") return;

    void visualizeForViolation(viewer, selected).then(applied => {
      if (applied) return;
      try {
        fitViolationSelection(
          viewer,
          dbId,
          elements,
          selected,
          onUniversalView
        );
      } catch (err) {
        console.debug("[Vera] viewer.select/fit failed for dbId", dbId, err);
      }
    });
  }, [
    viewer,
    ready,
    selected,
    selectedId,
    previewFacts,
    activeSheetId,
    onUniversalView,
    elements,
  ]);

  return (
    <main
      style={{
        position: "relative",
        background: "#111",
        overflow: "hidden",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div
        ref={containerRef}
        style={{
          position: "absolute",
          inset: 0,
          visibility: showEmptyPrompt ? "hidden" : "visible",
        }}
      />
      {showEmptyPrompt && <AccEmptyViewerPrompt />}
      <AccAnalysisOverlay />
      {!showEmptyPrompt && !ready && viewerUrn && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--fc2-text-dim)",
            fontSize: 13,
            pointerEvents: "none",
            zIndex: 5,
          }}
        >
          Loading sheet…
        </div>
      )}
      <MissingFactPrompt />
      <AccDocumentViewer />
    </main>
  );
}
